import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';

import TituloAdmin from '@components/TituloAdmin';
import Boton from '@components/Boton';
import Input from '@components/Input';
import { cajaPrincipalStyle } from '@styles/cajaPrincipal';
import { cajaContenedorAdminStyle } from '@styles/cajaContenedorAdmin';
import { tablaEstylos, tablaTheadEstylos, trStyle, thStyle, tdStyle } from '@styles/tablaEstylos';
import { colors } from '@styles/colors';
import { selectStyle } from '@styles/selecsEstylos';
import LoadingOverlay from '@components/LoadingOverlay';
import LabelCarga from '@components/LabelCarga';
import '@styles/modernTable.css';
import {
  getAllUsuariosApi,
  createUsuarioApi,
  updateUsuarioApi,
  deleteUsuarioApi
} from '@api/wp/usuarios/usuariosApi';
import { getAllRolesApi } from '@api/wp/roles/rolesApi';
import BotonIconoTabla from '@components/BotonIconoTabla';
import BotonIconoTablaDelete from '@components/BotonIconoTablaDelete';

function estadoIntToText(v) {
  if (v === 1 || v === '1') return 'Activo';
  if (v === 2 || v === '2') return 'Inactivo';
  return 'Pendiente';
}

function estadoTextToInt(s) {
  if (s === 'Activo') return 1;
  if (s === 'Inactivo') return 2;
  return 0;
}

function Usuarios() {
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [exitoModalOpen, setExitoModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editar, setEditar] = useState({ id: null, idRol: 0, nombre: '', alias: '', clave: '', estado: 0 });
  const [confirmarClaveNuevo, setConfirmarClaveNuevo] = useState('');
  const [confirmarClaveEditar, setConfirmarClaveEditar] = useState('');
  const [confirmEliminarOpen, setConfirmEliminarOpen] = useState(false);
  const [filaEliminarPendiente, setFilaEliminarPendiente] = useState(null);

  const [busqueda, setBusqueda] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  // Form creación: Estados independientes
  const [idRol, setIdRol] = useState(0);
  const [nombre, setNombre] = useState('');
  const [alias, setAlias] = useState('');
  const [clave, setClave] = useState('');
  
  const [roles, setRoles] = useState([]);

  const cargarRoles = async () => {
    try {
      const resp = await getAllRolesApi();
      const lista = resp?.result || resp?.data || resp || [];
                                     console.log('Roles cargados:', lista); // Debug log
      setRoles(Array.isArray(lista) ? lista : []);
    } catch (err) {
      console.error('Error al cargar roles:', err);
    }
  };

  const cargarUsuarios = async () => {
    try {
      setCargando(true);
      setError('');
      const resp = await getAllUsuariosApi();
      const lista = resp?.result || resp?.data || resp || [];
      const normalizados = Array.isArray(lista) ? lista.map(u => ({
        ...u,
        estado: estadoIntToText(u.estado)
      })) : [];
      setUsuarios(normalizados);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
      setError('No se pudieron cargar los usuarios');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargarUsuarios(); cargarRoles(); }, []);

  const cambiarValor = (index, campo, valor) => {
    const nuevas = [...usuarios];
    nuevas[index] = { ...nuevas[index], [campo]: valor };
    setUsuarios(nuevas);
  };

  const crearUsuario = async () => {
    try {
      setProcesando(true);
      setError('');
      setMensaje('');
      if (!Number(idRol)) {
        setError(`Debe seleccionar un rol válido (Valor actual: ${idRol})`);
        return;
      }
      if (!clave || !confirmarClaveNuevo) {
        setError('Debe ingresar y confirmar la clave');
        return;
      }
      if (String(clave) !== String(confirmarClaveNuevo)) {
        setError('Las claves no coinciden');
        return;
      }
      
      await createUsuarioApi({
        idRol: Number(idRol) || 0,
        nombre: nombre,
        alias: alias,
        clave: clave,
        estado: 1
      });

      // Resetear estados
      setIdRol(0);
      setNombre('');
      setAlias('');
      setClave('');
      setConfirmarClaveNuevo('');
      
      await cargarUsuarios();
      setMensaje('Usuario creado correctamente');
      setExitoModalOpen(true);
      setModalOpen(false);
    } catch (err) {
      console.error('Error al crear usuario:', err);
      setError('Error al crear usuario');
    } finally {
      setProcesando(false);
    }
  };

  const guardarFila = async (index) => {
    try {
      setProcesando(true);
      setError('');
      setMensaje('');
      const fila = usuarios[index];
      const id = fila?.id;
      if (!id) { setError('No se encontró el identificador'); return; }
      if (!Number(fila.idRol)) { setError('Seleccione un rol válido para el usuario'); return; }

      const payload = {
        idRol: Number(fila.idRol) || 0,
        nombre: fila.nombre,
        alias: fila.alias, // Added alias here too just in case, though not editable in table row yet
        clave: fila.clave,
        estado: estadoTextToInt(fila.estado)
      };
      await updateUsuarioApi(id, payload);
      setMensaje('Usuario actualizado correctamente');
    } catch (err) {
      console.error('Error al actualizar usuario:', err);
      setError('Error al actualizar usuario');
    } finally {
      setProcesando(false);
    }
  };

  const abrirEditar = (fila) => {
    if (!fila) return;
    setEditar({
      id: fila.id,
      idRol: fila.idRol ?? 0,
      nombre: fila.nombre ?? '',
      alias: fila.alias ?? '',
      clave: '', // Reset clave on edit open
      estado: fila.estado ?? 1
    });
    setConfirmarClaveEditar('');
    setEditModalOpen(true);
  };

  const solicitarEliminarUsuario = (fila) => {
    if (!fila?.id) return;
    setFilaEliminarPendiente({
      id: fila.id,
      nombre: fila.nombre ?? '',
      alias: fila.alias ?? '',
    });
    setConfirmEliminarOpen(true);
  };

  const cerrarConfirmEliminar = () => {
    setConfirmEliminarOpen(false);
    setFilaEliminarPendiente(null);
  };

  const confirmarEliminarUsuario = async () => {
    const id = filaEliminarPendiente?.id;
    if (!id) {
      cerrarConfirmEliminar();
      return;
    }
    setConfirmEliminarOpen(false);
    setFilaEliminarPendiente(null);
    try {
      setProcesando(true);
      setError('');
      setMensaje('');
      await deleteUsuarioApi(id);
      await cargarUsuarios();
      setMensaje('Usuario eliminado correctamente');
      setExitoModalOpen(true);
    } catch (err) {
      console.error('Error al eliminar usuario:', err);
      setError('Error al eliminar usuario');
    } finally {
      setProcesando(false);
    }
  };

  const guardarEditar = async () => {
    try {
      setProcesando(true);
      setError('');
      setMensaje('');
      const id = editar?.id;
      if (!id) { setError('No se encontró el identificador'); return; }
      if (!Number(editar.idRol)) { setError(`Seleccione un rol válido (Valor actual: ${editar.idRol})`); return; }
      if (editar.clave) {
        if (!confirmarClaveEditar) {
          setError('Debe confirmar la nueva clave');
          return;
        }
        if (String(editar.clave) !== String(confirmarClaveEditar)) {
          setError('Las claves no coinciden');
          return;
        }
      }
      const payload = {
        idRol: Number(editar.idRol) || 0,
        nombre: editar.nombre,
        alias: editar.alias,
        ...(editar.clave ? { clave: editar.clave } : {}),
        estado: estadoTextToInt(editar.estado)
      };
      console.log('Payload:', payload);
      await updateUsuarioApi(id, payload);
      await cargarUsuarios();
      setMensaje('Usuario actualizado correctamente');
      setExitoModalOpen(true);
      setEditModalOpen(false);
      setConfirmarClaveEditar('');
    } catch (err) {
      console.error('Error al actualizar usuario (modal):', err);
      setError('Error al actualizar usuario');
    } finally {
      setProcesando(false);
    }
  };

  // Filtrado
  const normalizar = (v) => (v || '').toString().toLowerCase();
  const usuariosFiltrados = usuarios.filter((u) => {
    const q = normalizar(busqueda);
    if (!q) return true;
    return (
      String(u.id).includes(q) ||
      String(u.idRol).includes(q) ||
      normalizar(u.nombre).includes(q) ||
      normalizar(u.alias).includes(q) ||
      normalizar(u.estado).includes(q)
    );
  });

  const descargarExcel = () => {
    const dataParaExcel = usuariosFiltrados.map(u => ({
      ID: u.id,
      Rol: (roles.find(r => String(r.id) === String(u.idRol))?.nombre) ?? u.idRol,
      Nombre: u.nombre ?? '',
      Alias: u.alias ?? '',
      Estado: u.estado ?? 0
    }));

    const csv = [ 
      Object.keys(dataParaExcel[0]).join(','),
      ...dataParaExcel.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'usuarios.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Sin Select2: el <select> de rol en modal nuevo usa HTML nativo

  // Tabla en modo lectura: sin Select2 en filas

  // Sin Select2: el <select> de rol en modal edición usa HTML nativo

  return (
    <div id="cajaPrincipal" style={cajaPrincipalStyle}>
      <LoadingOverlay visible={procesando} />
      <TituloAdmin
        titulo="Usuarios"
        imagen_titulo="servicios"
        subtitulo="Gestión de usuarios"
      />

      <div style={cajaContenedorAdminStyle}>
        {error && (
          <div style={{
            padding: '1.5vh 2vh', marginBottom: '2vh', borderRadius: '0.8vh', fontSize: '1.4vh', fontWeight: '500', fontFamily: 'Inter',
            border: `1px solid ${colors.errorText}`, backgroundColor: colors.errorShadow, color: colors.errorText
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}
        {mensaje && (
          <div style={{
            padding: '1.5vh 2vh', marginBottom: '2vh', borderRadius: '0.8vh', fontSize: '1.4vh', fontWeight: '500', fontFamily: 'Inter',
            border: `1px solid ${colors.primary}`, backgroundColor: colors.resumenBg, color: colors.primaryText
          }}>
            {mensaje}
          </div>
        )}

        {/* Búsqueda */}
        <div style={{ display: 'flex', gap: '1vh', marginBottom: '2vh', alignItems: 'center' }}>
          <label style={{ fontFamily: 'Inter', fontSize: '1.5vh', color: colors.gray700 }}>Buscar:</label>
          <Input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="ID, rol, nombre o estado"
            ancho="40vh"
            icono="buscar"
          />
          <Boton label="Limpiar" onClick={() => setBusqueda('')} color={colors.secondaryButton} ancho="14vh" alto="4.5vh" />
        </div>

        {/* Botones de acción */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1vh', marginBottom: '2vh' }}>
          <Boton label="Nuevo Usuario" onClick={() => setModalOpen(true)} color={colors.accent} ancho="18vh" alto="4.5vh" />
        </div>

        {/* Tabla */}
        <div className="modern-table-container">
          <table id="tabla-usuarios" className="modern-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Rol</th>
                <th>Codigo</th>
                <th>Alias</th>
                <th>Estado</th>
                <th style={{ textAlign: 'center' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {cargando ? (
                <tr><td colSpan={6}><LabelCarga nombre="usuarios" /></td></tr>
              ) : usuariosFiltrados.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2vh', color: colors.secondaryButton }}>No hay usuarios registrados</td></tr>
              ) : (
                usuariosFiltrados.map((u, idx) => (
                  <tr key={u.id || idx}>
                    <td>{u.id}</td>
                    <td>{(roles.find(r => String(r.id) === String(u.idRol))?.nombre) ?? u.idRol}</td>
                    <td>{u.nombre ?? ''}</td>
                    <td>{u.alias ?? ''}</td>
                    <td>{u.estado ?? 0}</td>
                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', gap: '1vh', justifyContent: 'center', alignItems: 'center' }}>
                        <BotonIconoTabla
                          icono="edit"
                          tooltipText="Editar"
                          onClick={() => abrirEditar(u)}
                          disabled={procesando}
                        />
                        <BotonIconoTablaDelete
                          tooltipText="Eliminar"
                          onClick={() => solicitarEliminarUsuario(u)}
                          disabled={procesando}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Modal de creación de usuario */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="md">
        <DialogTitle style={{ fontFamily: 'Inter', fontWeight: 700 }}>Crear Usuario</DialogTitle>
        <DialogContent dividers>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2vh' }}>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ fontFamily: 'Inter', fontSize: '1.6vh', marginBottom: '0.6vh', display: 'block' }}>Rol</label>
              <select 
                value={idRol} 
                onChange={(e) => setIdRol(e.target.value)} 
                style={selectStyle}
              >
                <option value="0">-- Seleccione un rol --</option>
                {(roles || []).map(r => (
                  <option key={r.id} value={r.id}>{r.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontFamily: 'Inter', fontSize: '1.6vh', marginBottom: '0.6vh', display: 'block' }}>Nombre</label>
              <Input
                ancho="100%"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Codigo"
                autoComplete="off"
              />
            </div>
            <div>
              <label style={{ fontFamily: 'Inter', fontSize: '1.6vh', marginBottom: '0.6vh', display: 'block' }}>Alias</label>
              <Input
                ancho="100%"
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
                placeholder="Alias"
                autoComplete="off"
              />
            </div>
            <div>
              <label style={{ fontFamily: 'Inter', fontSize: '1.6vh', marginBottom: '0.6vh', display: 'block' }}>Clave</label>
              <Input
                ancho="100%"
                value={clave}
                onChange={(e) => setClave(e.target.value)}
                placeholder="Clave"
                type="password"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label style={{ fontFamily: 'Inter', fontSize: '1.6vh', marginBottom: '0.6vh', display: 'block' }}>Confirmar Clave</label>
              <Input
                ancho="100%"
                value={confirmarClaveNuevo}
                onChange={(e) => setConfirmarClaveNuevo(e.target.value)}
                placeholder="Confirmar Clave"
                type="password"
                autoComplete="new-password"
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <button
            onClick={() => setModalOpen(false)}
            style={{
              backgroundColor: colors.gray200,
              color: colors.primaryText,
              border: 'none',
              borderRadius: '6px',
              padding: '0.5rem 1rem',
              fontSize: '1.6vh',
              fontFamily: 'Inter',
              cursor: 'pointer'
            }}
          >
            Cancelar
          </button>
          <button
            onClick={crearUsuario}
            disabled={procesando}
            style={{
              backgroundColor: colors.primary,
              color: colors.white,
              border: 'none',
              borderRadius: '6px',
              padding: '0.5rem 1rem',
              fontSize: '1.6vh',
              fontFamily: 'Inter',
              fontWeight: '600',
              cursor: procesando ? 'not-allowed' : 'pointer'
            }}
          >
            {procesando ? 'Creando...' : 'Guardar'}
          </button>
        </DialogActions>
      </Dialog>
      {/* Modal de edición de usuario */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} fullWidth maxWidth="md">
        <DialogTitle style={{ fontFamily: 'Inter', fontWeight: 700 }}>Editar Usuario</DialogTitle>
        <DialogContent dividers>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2vh' }}>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ fontFamily: 'Inter', fontSize: '1.6vh', marginBottom: '0.6vh', display: 'block' }}>Rol</label>
              <select 
                value={editar.idRol} 
                onChange={(e) => setEditar({ ...editar, idRol: e.target.value })} 
                style={selectStyle}
              >
                <option value="0">-- Seleccione un rol --</option>
                {(roles || []).map(r => (
                  <option key={r.id} value={r.id}>{r.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontFamily: 'Inter', fontSize: '1.6vh', marginBottom: '0.6vh', display: 'block' }}>Nombre</label>
              <Input
                ancho="100%"
                value={editar.nombre}
                onChange={(e) => setEditar({ ...editar, nombre: e.target.value })}
                placeholder="Nombre"
                autoComplete="off"
              />
            </div>
            <div>
              <label style={{ fontFamily: 'Inter', fontSize: '1.6vh', marginBottom: '0.6vh', display: 'block' }}>Alias</label>
              <Input
                ancho="100%"
                value={editar.alias}
                onChange={(e) => setEditar({ ...editar, alias: e.target.value })}
                placeholder="Alias"
                autoComplete="off"
              />
            </div>
            <div>
              <label style={{ fontFamily: 'Inter', fontSize: '1.6vh', marginBottom: '0.6vh', display: 'block' }}>Clave (Dejar en blanco para no cambiar)</label>
              <Input
                ancho="100%"
                value={editar.clave}
                onChange={(e) => setEditar({ ...editar, clave: e.target.value })}
                placeholder="Clave"
                type="password"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label style={{ fontFamily: 'Inter', fontSize: '1.6vh', marginBottom: '0.6vh', display: 'block' }}>Confirmar Clave</label>
              <Input
                ancho="100%"
                value={confirmarClaveEditar}
                onChange={(e) => setConfirmarClaveEditar(e.target.value)}
                placeholder="Confirmar Clave"
                type="password"
                autoComplete="new-password"
                disabled={!editar.clave}
              />
            </div>
            <div>
              <label style={{ fontFamily: 'Inter', fontSize: '1.6vh', marginBottom: '0.6vh', display: 'block' }}>Estado</label>
              <select value={editar.estado}
                onChange={(e) => setEditar({ ...editar, estado: e.target.value })}
                style={{ width: '100%', padding: '1.2vh', borderRadius: '0.8vh', border: `2px solid ${colors.gray200}`, fontSize: '1.6vh', backgroundColor: colors.white, outline: 'none', fontFamily: 'Inter' }}>
                <option value="0">Activo</option>
                <option value="1">Inactivo</option>
              </select>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <button
            onClick={() => setEditModalOpen(false)}
            style={{
              backgroundColor: colors.gray200,
              color: colors.primaryText,
              border: 'none',
              borderRadius: '6px',
              padding: '0.5rem 1rem',
              fontSize: '1.6vh',
              fontFamily: 'Inter',
              cursor: 'pointer'
            }}
          >
            Cancelar
          </button>
          <button
            onClick={guardarEditar}
            disabled={procesando}
            style={{
              backgroundColor: colors.primary,
              color: colors.white,
              border: 'none',
              borderRadius: '6px',
              padding: '0.5rem 1rem',
              fontSize: '1.6vh',
              fontFamily: 'Inter',
              fontWeight: '600',
              cursor: procesando ? 'not-allowed' : 'pointer'
            }}
          >
            {procesando ? 'Guardando...' : 'Guardar'}
          </button>
        </DialogActions>
      </Dialog>
      {/* Confirmación eliminar usuario */}
      <Dialog open={confirmEliminarOpen} onClose={cerrarConfirmEliminar} fullWidth maxWidth="sm">
        <DialogTitle style={{ fontFamily: 'Inter', fontWeight: 700 }}>Eliminar usuario</DialogTitle>
        <DialogContent dividers>
          <div style={{ fontFamily: 'Inter', fontSize: '1.6vh', color: colors.gray700 }}>
            <p style={{ margin: '0 0 1vh 0' }}>¿Está seguro que desea eliminar este usuario?</p>
            {filaEliminarPendiente && (
              <p style={{ margin: 0, fontWeight: 600, color: colors.primaryText }}>
                {[filaEliminarPendiente.nombre, filaEliminarPendiente.alias].filter(Boolean).join(' · ') || `ID: ${filaEliminarPendiente.id}`}
              </p>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Boton
            label="No, cancelar"
            onClick={cerrarConfirmEliminar}
            disabled={procesando}
            color={colors.secondaryButton}
            ancho="18vh"
            alto="4.5vh"
          />
          <Boton
            label={procesando ? 'Eliminando...' : 'Sí, eliminar'}
            onClick={confirmarEliminarUsuario}
            disabled={procesando}
            color={colors.delete}
            ancho="18vh"
            alto="4.5vh"
          />
        </DialogActions>
      </Dialog>
      {/* Modal de éxito */}
      <Dialog open={exitoModalOpen} onClose={() => { setExitoModalOpen(false); }} fullWidth maxWidth="xs">
        <DialogTitle style={{ fontFamily: 'Inter', fontWeight: 700 }}>Éxito</DialogTitle>
        <DialogContent dividers>
          <div style={{ fontFamily: 'Inter', fontSize: '1.6vh' }}>
            {mensaje}
          </div>
        </DialogContent>
        <DialogActions>
          <button
            onClick={() => { setExitoModalOpen(false); setMensaje(''); }}
            style={{
              backgroundColor: colors.primary,
              color: colors.white,
              border: 'none',
              borderRadius: '6px',
              padding: '0.5rem 1rem',
              fontSize: '1.6vh',
              fontFamily: 'Inter',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Aceptar
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default Usuarios;